import { Box, FormControl, FormHelperText } from '@mui/material';
import { FC, useEffect, useMemo, useState } from 'react';
import { DropzoneOptions, FileRejection, useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { InputFieldLabel } from '../shared/fieldLabel';
import { Dropzone } from './dropzone';
import { DropzoneContent } from './dropzoneContent';
import { FilePreview } from './filePreview';
import _ from 'lodash';
import { Base64FileField } from '@pxtra-client-lib-legacy/gen';
import * as Mime from 'mime';
import Util from '../../../../../misc/util';
import { ImageCropperModal } from './cropper';

export type FileWithPreview = File & { preview: string; errors?: Array<unknown>; file?: BlobPart };

type Props = {
  onChange: (files?: Array<FileWithPreview>) => void;
  onDelete?: (file: FileWithPreview) => void;
  initFiles?: Array<FileWithPreview> | Base64FileField | null;
  options?: Pick<DropzoneOptions, 'accept' | 'maxFiles' | 'maxSize'>;
  recommendedResolution?: [number, number];
  disabled?: boolean;
  disableDelete?: boolean;
  hideFileInfo?: boolean;
  label?: string;
  required?: boolean;
  error?: boolean;
  helperText?: string;
  name?: string;
  tabIndex?: number;
  dropzoneWidth?: string;
  dropzoneHeight?: string;
  dataTestId?: string;
  showOptions?: boolean;
  cropPhoto?: boolean;
};
export type FileUploadProps = Props;

export const FileUpload: FC<Props> = ({
  onChange,
  onDelete,
  initFiles = [],
  options,
  recommendedResolution,
  disabled,
  disableDelete = false,
  hideFileInfo = false,
  label,
  required,
  error,
  helperText,
  tabIndex,
  dropzoneWidth = undefined,
  dropzoneHeight = undefined,
  dataTestId = undefined,
  showOptions = true,
  cropPhoto = false
}) => {
  const { t } = useTranslation();

  const renderOptions = (options: Props['options'], resolution?: [number, number]) => {
    if (!options) return undefined;
    const { accept, maxFiles, maxSize } = options;
    const acceptString = accept
      ? Object.entries(accept)
          .map(([, value]) => {
            const extensions = value.map((ext) => ext.replace(/^\./, '')).join('/');
            return extensions;
          })
          .join(', ')
      : undefined;
    const maxFilesString = maxFiles ? t('form:MAX_FILES', { count: maxFiles }) : undefined;
    const maxSizeString = maxSize
      ? t('form:MAX_SIZE', { size: `${Math.floor(maxSize / (1024 * 1024))} MB` })
      : undefined;
    const recommendedResolution = resolution
      ? t('form:RECOMMENDED_RESOLUTION', { x_value: resolution[0], y_value: resolution[1] })
      : undefined;

    return (
      <FormHelperText className="m-0">
        {[acceptString, maxFilesString, maxSizeString, recommendedResolution]
          .filter((str) => str)
          .join(' | ')}
      </FormHelperText>
    );
  };

  const [files, setFiles] = useState<Array<FileWithPreview>>(
    Array.isArray(initFiles) ? initFiles : []
  );
  const [internalError, setinternalError] = useState<string>();
  const [dropRejected, setDropRejected] = useState(false);

  const [cropperOpen, setCropperOpen] = useState(false);
  const [imageToCrop, setImageToCrop] = useState<{ file: File; preview: string } | null>(null);

  const dropzoneDisabled =
    disabled || (options?.maxFiles ? files.length >= options.maxFiles : false);

  const isImageFile = (file: File) => {
    return file.type.startsWith('image/');
  };

  const { isFocused, isDragAccept, isDragReject, getRootProps, getInputProps } = useDropzone({
    ...options,
    disabled: dropzoneDisabled,
    onDropAccepted: (acceptedFiles: Array<File>) => {
      setDropRejected(false);
      setinternalError(undefined);

      if (cropPhoto && acceptedFiles.length > 0 && isImageFile(acceptedFiles[0])) {
        const file = acceptedFiles[0];
        const preview = URL.createObjectURL(file);
        setImageToCrop({ file, preview });
        setCropperOpen(true);
      } else {
        setFiles([
          ...files,
          ...acceptedFiles.map<FileWithPreview>((file) =>
            Object.assign(file, {
              preview: URL.createObjectURL(file)
            })
          )
        ]);
      }
    },
    onDropRejected: (rejections: Array<FileRejection>) => {
      const errors = [];

      for (const rej of rejections) {
        for (const err of rej.errors) {
          const codes = errors.map((err) => err.code);
          if (!codes.includes(err.code)) {
            switch (err.code) {
              case 'too-many-files':
                errors.push({ ...err, options: { count: options?.maxFiles } });
                break;
              case 'file-invalid-type':
                errors.push({
                  ...err,
                  options: { context: 'type', type: err.message.split('File type must be ')[1] }
                });
                break;
              case 'file-too-large':
                errors.push({
                  ...err,
                  options: {
                    context: 'size',
                    size: `${Math.floor((options?.maxSize || 0) / (1024 * 1024))} MB`
                  }
                });
                break;
              default:
                break;
            }
          }
        }
      }

      setinternalError(
        errors.map((err) => t(`form:${err.code.toUpperCase()}`, err.options)).join('. ')
      );
      return setDropRejected(true);
    }
  });

  const handleCropComplete = (croppedFile: File) => {
    const fileWithPreview = Object.assign(croppedFile, {
      preview: URL.createObjectURL(croppedFile)
    }) as FileWithPreview;

    setFiles([...files, fileWithPreview]);

    // Cleanup
    if (imageToCrop) {
      URL.revokeObjectURL(imageToCrop.preview);
    }
    setImageToCrop(null);
  };

  const handleCropperClose = () => {
    if (imageToCrop) {
      URL.revokeObjectURL(imageToCrop.preview);
    }
    setImageToCrop(null);
    setCropperOpen(false);
  };

  const filePreviews = useMemo(
    () =>
      files.map((file, index) => {
        return (
          <FilePreview
            key={`${file.name}${index}`}
            name={file.name}
            size={file.size}
            preview={Util.getURLfromFileWithPreview(file)}
            type={file.type}
            onDelete={() => {
              setFiles(files.filter((innerFile) => innerFile !== file));
              if (onDelete) onDelete(file);
            }}
            width={dropzoneWidth}
            height={dropzoneHeight}
            disableDelete={disableDelete}
            hideFileInfo={hideFileInfo}
            dataTestId={dataTestId}
          />
        );
      }),
    [files]
  );

  useEffect(() => {
    onChange(files);
  }, [files]);

  useEffect(() => {
    if (Array.isArray(initFiles) && !_.isEqual(initFiles, files) && initFiles.length > 0) {
      setFiles(initFiles);
    }
    if (initFiles && !Array.isArray(initFiles)) {
      const previewFile: FileWithPreview = {
        ...new File([''], ''),
        name: initFiles.name,
        preview: initFiles.url,
        size: initFiles.size,
        type: Mime.getType(Util.getFileSuffix(initFiles.url)) ?? ''
      };
      setFiles([previewFile]);
    }
  }, [initFiles]);

  useEffect(() => {
    return () => files.forEach((file) => URL.revokeObjectURL(file.preview));
  }, []);

  return (
    <FormControl fullWidth>
      {label && <InputFieldLabel label={label} required={required} />}
      <Box className="flex gap-1 flex-wrap p-2 pl-4 pr-0 ">
        {!dropzoneDisabled && (
          <Dropzone
            {...getRootProps({ isFocused, isDragAccept, isDragReject })}
            width={dropzoneWidth ? dropzoneWidth : undefined}
            height={dropzoneHeight ? dropzoneHeight : undefined}
            disabled={dropzoneDisabled}
            dropRejected={dropRejected}
            tabIndex={tabIndex ?? 0}
          >
            <input required={required} {...getInputProps()} />
            <DropzoneContent />
          </Dropzone>
        )}
        {filePreviews}
      </Box>

      {internalError || showOptions ? (
        <Box>
          {internalError ? (
            <FormHelperText sx={{ color: 'error.main', marginTop: '4px', marginY: 'auto' }}>
              {internalError ? internalError : error && helperText}
            </FormHelperText>
          ) : (
            renderOptions(options, recommendedResolution)
          )}
        </Box>
      ) : (
        <></>
      )}

      {cropPhoto && imageToCrop && (
        <ImageCropperModal
          open={cropperOpen}
          imageSrc={imageToCrop.preview}
          fileName={imageToCrop.file.name}
          onClose={handleCropperClose}
          onCropComplete={handleCropComplete}
        />
      )}
    </FormControl>
  );
};

export const isFileWithPreview = (arg: FileRejection | FileWithPreview): arg is FileWithPreview =>
  arg !== undefined;
